/// QuestionStelling.js

import React, { useState, useEffect, useRef, memo, useCallback, useContext } from "react";
import remarkGfm from 'remark-gfm';
import styled from "styled-components";
import DOMPurify from 'dompurify'; // Import DOMPurify
import { checkAndInitializeDB, saveToDB, getFromDB } from '../../../component/indexedDB/indexedDB'; // Adjust the import path as necessary
import QuestionPublicLink from "../../../component/PublicLink/PublicLink";
import Modal from "../../../component/modal/modal"; // Corrected the path from "compontent" to "component"
import { CloseButton, QuizMessagePopUp } from "../../../component/modal/QuizMessagePopUp";
import { 
  QuizScreen, 
  QuizTitle, 
  QuizDescription, 
  QuizButton, 
  QuizMessage, 
  QuizForm, 
  QuizFormHorizontal, 
  tint, 
  darken, 
  lighten,
  QuizMessageMedia, // Import the new styled component
  QuizDescriptionDiv
} from "../../GeneralStyles";
import ScaleContext from "../../../component/QuizScale/ScaleContext";
import QuizTable from "../../../component/QuizTable/QuizTable";
import QuizMarkdown from "../../../component/Markdown/QuizMarkdown";


// Styled Components for Image Wrapper and Fullscreen Icon
const ImageWrapper = styled.span`
  position: relative;
  display: inline-block;

  img {
    width: 100%;
    display: block;
  }
`;

const FullscreenIcon = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 33px;
  height: 33px;
  background-color: white;
  border-radius: 50%;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20512%20512%22%3E%3Cpath%20fill%3D%22%23031037%22%20d%3D%22M344%200L488%200c13.3%200%2024%2010.7%2024%2024l0%20144c0%209.7-5.8%2018.5-14.8%2022.2s-19.3%201.7-26.2-5.2l-39-39-87%2087c-9.4%209.4-24.6%209.4-33.9%200l-32-32c-9.4-9.4-9.4-24.6%200-33.9l87-87L327%2041c-6.9-6.9-8.9-17.2-5.2-26.2S334.3%200%20344%200zM168%20512L24%20512c-13.3%200-24-10.7-24-24L0%20344c0-9.7%205.8-18.5%2014.8-22.2s19.3-1.7%2026.2%205.2l39%2039%2087-87c9.4-9.4%2024.6-9.4%2033.9%200l32%2032c9.4%209.4%209.4%2024.6%200%2033.9l-87%2087%2039%2039c6.9%206.9%208.9%2017.2%205.2%2026.2s-12.5%2014.8-22.2%2014.8z%22%2F%3E%3C%2Fsvg%3E");
  background-size: 16px;
  background-repeat: no-repeat;
  background-position: center;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #000; /* Optional: Add focus styles for accessibility */
  }
`;

// Memoized MarkdownImage Component
const MarkdownImage = memo(({ src, alt, openModal }) => (
  <ImageWrapper>
    <img src={src} alt={alt} />
    <FullscreenIcon
      onClick={(e) => {
        e.preventDefault();    // Prevent default behavior
        e.stopPropagation();   // Stop event from bubbling up to the label
        openModal(src);
      }}
      aria-label="View Fullscreen"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          openModal(src);
        }
      }}
    />
  </ImageWrapper>
));

const QuestionStellingItem = styled.div`
  cursor: pointer;
  position: relative;
  width: 100%;
  height: 100%;
  & input[type="radio"],
  & input[type="checkbox"] {
    position: absolute;   
    opacity: 0;
    & + label { 
      ${(props) => props.bgColor && `background-color: ${tint(props.bgColor, 100)};`}
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 7px;
      border-radius: 10px;
      border-width: 1px;
      margin-bottom:0;
      border-style: solid;
      ${(props) => props.bgColor && `border-color: ${tint(props.bgColor, 80)};`}
      box-shadow: 0 4px 6px -1px #001d2910, 0 2px 4px -2px #001d2910;
      font-size: max(1rem, 18px);/*1.2em;*/
      cursor: pointer;
      font-family: 'Arial', 'Helvetica Neue', 'Helvetica', 'Nimbus Sans L', 'Liberation Sans', 'Arimo', 'DejaVu Sans', 'Sans-Serif';
      &:hover  {
        ${(props) => props.bgColor && `background-color: ${darken(props.bgColor, 15)};`}
        ${(props) => props.txtColor && `color: ${darken(props.txtColor, 15)};`}
        ${(props) => props.bgColor && `border-color: ${darken(props.bgColor, 50)};`}
      }
      > p:has(span:only-child) {
        line-height:0;
      }
      p {
        white-space: pre-line;
      }
    }
    &:checked + label {
      ${(props) => props.bgColor && `background-color: ${props.bgColor};`}
      ${(props) => props.bgColor && `border-color: ${darken(props.bgColor, 50)};`}
      ${(props) => props.txtColor && `color: ${props.txtColor};`}
      :hover {
        ${(props) => props.bgColor && `background-color: ${darken(props.bgColor,30)};`}
        ${(props) => props.txtColor && `color: ${props.txtColor};`}
      }   
    }
    &:disabled {
      & + label {
        ${(props) => props.txtColor && `color: ${lighten(props.txtColor,99)};`}
        :hover {
            ${(props) => props.bgColor && `background-color: ${tint(props.bgColor,99)};`}  
            ${(props) => props.txtColor && `color: ${lighten(props.txtColor,99)};`}
            ${(props) => props.bgColor && `border-color: ${tint(props.bgColor, 80)};`}
            cursor: not-allowed;
          }
      }
      &:checked {
        & + label {
          ${(props) => props.txtColor && `color: ${props.txtColor};`}
          :hover {
            ${(props) => props.bgColor && `background-color: ${props.bgColor};`}  
            ${(props) => props.txtColor && `color: ${props.txtColor};`}
            ${(props) => props.bgColor && `border-color: ${darken(props.bgColor, 50)};`}
          }   
        }
      }    
    }        
  }
  p:first-of-type {
    margin:0;
  }
  img {
    width: 100%;
    margin:0;
    padding:0;
    padding-inline:0;
  }
`;

const QuestionStelling = ({ question, overViewPage, enableNavigationButton }) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [message, setMessage] = useState(null);
  const [messageMedia, setMessageMedia] = useState(null); // New state for media
  const [attempts, setAttempts] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [modalImage, setModalImage] = useState(null); // State for Modal
  const formRef = useRef(null);
  const scaleFactor = useContext(ScaleContext);

  useEffect(() => {
    const formElement = formRef.current;
    if (formElement && formElement.children.length > 4) {
      formElement.classList.add('wrap');
    } else {
      formElement.classList.remove('wrap');
    }
  }, [question]);

  useEffect(() => {
    const dbName = sessionStorage.getItem('currentSessionName') || 'Leerling';
    checkAndInitializeDB(dbName);
    if(dbName !== 'Leerling'){
      getFromDB(dbName).then(data => {
        const attemptData = data.find(item => item.id === question.quiz_item_id);
        if (attemptData) {
          setAttempts(attemptData.attempts || 0);
          if (attemptData.answer) {
            const answers = {};
            attemptData.answer.forEach(answer => {
              answers[answer] = true;
            });
            setSelectedAnswers(answers);
            enableNavigationButton(); // Enable navigation if there's already an answer
          }
          if (question.maximum_attempts > 0 && attemptData.attempts >= question.maximum_attempts) {
            setIsDisabled(true);
          }
        }
      });
    }
  }, [question.quiz_item_id, question.maximum_attempts, enableNavigationButton, question]);

  const handleChange = (id) => {
    if (question.Antwoord_items.filter(item => item.is_correct_answer).length > 1) {
      setSelectedAnswers((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    } else {
      setSelectedAnswers({ [id]: true });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dbName = sessionStorage.getItem('currentSessionName') || 'Leerling';
    if (question.maximum_attempts > 0 && attempts >= question.maximum_attempts) {
      setMessage(DOMPurify.sanitize("Je hebt het maximale aantal pogingen bereikt."));
      setMessageMedia(null); // No media for this message
      return;
    }

    // Removed console.info to prevent potential side effects

    const correctAnswers = question.Antwoord_items.filter(item => item.is_correct_answer).map(item => item.id);
    const selectedAnswerIds = Object.keys(selectedAnswers).filter(id => selectedAnswers[id]).map(Number); // Convert to numbers
    const filteredMessageText = question.Antwoord_items.find(item => selectedAnswerIds.includes(item.id));

    if (question.is_open_reflectie_vraag === true) {
      if (selectedAnswerIds.length > 0) {
        setIsSuccess(true);
        if (filteredMessageText && filteredMessageText.custom_succes_text && filteredMessageText.custom_succes_text.length > 0) {
          setMessage(DOMPurify.sanitize(filteredMessageText.custom_succes_text));
        } else {
          setMessage(DOMPurify.sanitize(question.general_succes_text));
        }
        setMessageMedia(question.general_succes_media); // Set success media
      } else {
        setIsSuccess(false);
        setMessage(DOMPurify.sanitize(question.general_error_text));
        setMessageMedia(question.general_error_media); // Set error media
      }
    } else {
      // Define allCorrect based on correctness
      const allCorrect = correctAnswers.length === selectedAnswerIds.length && correctAnswers.every(id => selectedAnswerIds.includes(id));
      setIsSuccess(allCorrect);

      if (!allCorrect) {
        const firstIncorrectAnswer = question.Antwoord_items.find(item => !item.is_correct_answer && selectedAnswerIds.includes(item.id));
        if (firstIncorrectAnswer) {
          if(firstIncorrectAnswer.custom_error_text && firstIncorrectAnswer.custom_error_text !== ""){
            setMessage(DOMPurify.sanitize(firstIncorrectAnswer.custom_error_text));
          } else {
            setMessage(DOMPurify.sanitize(question.general_error_text));
          }
        } else {
          setMessage(DOMPurify.sanitize(question.general_error_text));
        }
        setMessageMedia(question.general_error_media); // Set error media
      } else {
        if (filteredMessageText && filteredMessageText.custom_succes_text && filteredMessageText.custom_succes_text.length > 0) {
          setMessage(DOMPurify.sanitize(filteredMessageText.custom_succes_text));
        } else {
          setMessage(DOMPurify.sanitize(question.general_succes_text));
        }
        setMessageMedia(question.general_succes_media); // Set success media
      }

      const attempt = {
        id: question.quiz_item_id,
        answer: selectedAnswerIds,
        success: allCorrect, // Use allCorrect instead of isAllCorrect
        attempts: attempts + 1,
      };
      saveToDB([attempt], dbName);
      setAttempts(attempts + 1);
      // Enable the NavigationButton
      enableNavigationButton();

      if (question.maximum_attempts > 0 && attempts + 1 >= question.maximum_attempts) {
        setIsDisabled(true);
      }
    }
  };

  // Stable function to open modal using useCallback
  const openModal = useCallback((src) => {
    setModalImage(src);
  }, []);
  const handleClosePopup = () => {
    setMessage(null);
    setMessageMedia([]);
  };

  // Helper function to render media based on mime type
  const renderMedia = (mediaItems) => {
    if (!mediaItems || mediaItems.length === 0) return null;

    return (
      <QuizMessageMedia>
        {mediaItems.map(media => {
          if (media.mime && media.mime.startsWith('image/')) {
            return <img style={{width:'50px'}} key={media.id} src={media.url} alt={media.name || 'media'} />;
          } else if (media.mime && media.mime.startsWith('audio/')) {
            return <audio key={media.id} autoPlay src={media.url} />;
          } else if (media.embed) { // Handle embedded media like iframes
            return <div key={media.id} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(media.embed) }} />;
          } else {
            return null; // You can handle other media types if needed
          }
        })}
      </QuizMessageMedia>
    );
  };

  return (
    <QuizScreen bgColor={question.theme_color} bgVisible={overViewPage} id={question.quiz_item_id}>
      <QuestionPublicLink overViewPage={overViewPage} type="question" quiz_item_id={question.quiz_item_id} />
      <QuizTitle>{question.title}</QuizTitle>
      <QuizDescriptionDiv><QuizMarkdown remarkPlugins={[remarkGfm]} components={{table: QuizTable,}}>{question.description}</QuizMarkdown></QuizDescriptionDiv>
      <QuizForm onSubmit={handleSubmit}>
        <QuizFormHorizontal ref={formRef} scaleFactor={scaleFactor}>
          {question.Antwoord_items.map((antwoordItem) => {
            const isMultipleCorrect = question.Antwoord_items.filter(item => item.is_correct_answer).length > 1;
            return (
              <QuestionStellingItem 
                bgColor={question.theme_color} 
                txtColor={question.theme_text_color} 
                className="questionStellingItem" 
                key={antwoordItem.id} 
                data-id={antwoordItem.id}
              >
                <input
                  type={isMultipleCorrect ? "checkbox" : "radio"}
                  id={`answer_${antwoordItem.id}`}
                  name="answer"
                  checked={!!selectedAnswers[antwoordItem.id]}
                  onChange={() => handleChange(antwoordItem.id)}
                  disabled={isDisabled}
                />
                <label htmlFor={`answer_${antwoordItem.id}`}>
                  <QuizMarkdown components={{
                    img: ({node, ...props}) => <MarkdownImage {...props} openModal={openModal} /> 
                  }}>
                    {antwoordItem.content}
                  </QuizMarkdown>
                </label>
              </QuestionStellingItem>
            );
          })}
        </QuizFormHorizontal>
        {question.show_submit_button !== false && (
          <QuizButton type="submit" bgColor={question.theme_color} txtColor={question.theme_text_color} disabled={isDisabled}>
            {question.submit_button_text}
          </QuizButton>
        )}
        {(message || (messageMedia && messageMedia.length > 0)) && (
        <QuizMessagePopUp>
          <CloseButton onClick={handleClosePopup} aria-label="Close Popup">&times;</CloseButton>
          {/* Render Media First */}
          {renderMedia(messageMedia)}
          {/* Updated QuizMessage to render HTML */}
          <QuizMessage className="message">
            <QuizMarkdown>{message}</QuizMarkdown>
          </QuizMessage>
        </QuizMessagePopUp>
        )}
      </QuizForm>

      {/* Modal for Fullscreen Image */}
      {modalImage && (
        <Modal onClose={() => setModalImage(null)}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <img
              src={modalImage}
              alt="Fullscreen View"
              style={{
                width: "95%",
                height: "auto",
              }}
            />
          </div>
        </Modal>
      )}
    </QuizScreen>
  );
};

export default QuestionStelling;
